import React from "react"
import { Footer } from "./Footer"
import { FormContainer, Worksheet, LabeledInput } from "uikit"
import { Dropdown } from "semantic-ui-react"
import { Link } from "react-router-dom"
import { useStore } from "store"

const getOwner = (incomes, income_id) => {
  let income = incomes.find(income => income.id === income_id)
  if (income) {
    return income.person_id
  }
  return null
}

const SEPPlanForm = ({
  data: {
    fields: { income_id, account_id, contribution_rate },
    isModified,
    onSubmit,
    isPending,
    hasError,
    error
  },
  submitLabel = "Done",
  submitHeader = "",
  isNew,
  onDelete,
  incomes,
  accounts,
  accountOptionFilters,
  incomeSourceOptions
}) => {
  const owner_id = getOwner(incomes, income_id.value)

  const connectedAccountsOptions = accounts
    .filter(accountOptionFilters.connectedAccount(accounts, owner_id))
    .map(account => ({
      text: account.name,
      value: account.id
    }))

  const connectedAccountType = accountOptionFilters.connectedAccountData.type
  const connectedAccountName = accountOptionFilters.connectedAccountData.label

  return (
    <Worksheet error={error}>
      <Worksheet.Body>
        <Worksheet.Row
          label="Which income source is associated with this plan?"
          paddedField
        >
          <Worksheet.Field error={income_id.error}>
            <Dropdown
              placeholder="Income Source"
              selection
              options={incomeSourceOptions}
              {...income_id}
              onChange={(event, data) => {
                income_id.onChange(event, data)
                account_id.onChange(event, { value: "" })
              }}
            />
          </Worksheet.Field>
        </Worksheet.Row>
        <Worksheet.Row
          label="Which account is connected with the plan?"
          paddedField
        >
          <Worksheet.Field error={account_id.error}>
            {connectedAccountsOptions && connectedAccountsOptions.length > 0 ? (
              <Dropdown
                fluid
                placeholder="Account"
                data-testid="account_id"
                selection
                options={connectedAccountsOptions}
                {...account_id}
              />
            ) : (
              <Link
                to={`/organize/accounts+property/accounts/new/${connectedAccountType}`}
                className="navlink"
              >
                Go back to Accounts+Property to add {connectedAccountName}{" "}
                account...
              </Link>
            )}
          </Worksheet.Field>
        </Worksheet.Row>
        <Worksheet.Row
          label="What percent of your salary does your employer contribute?"
          paddedField
        >
          <Worksheet.Field error={contribution_rate.error}>
            <LabeledInput
              {...contribution_rate}
              label=""
              iconText="%"
              placeholder="0"
            />
          </Worksheet.Field>
        </Worksheet.Row>
      </Worksheet.Body>
      <Footer
        isNew={isNew}
        submitLabel={submitLabel}
        onSubmit={onSubmit}
        onDelete={onDelete}
        isModified={isModified}
        deleteLabel="Delete SEP Plan"
        error={error}
      />
    </Worksheet>
  )
}

const SEPPlanFormContainer = FormContainer(
  {
    income_id: {
      type: "string"
    },
    account_id: {
      type: "string",
      required: true
    },
    contribution_rate: {
      type: "decimal",
      minValue: 0,
      maxValue: 100
    }
  },
  (fields, ...form) => {}
)(SEPPlanForm)

const SEPPlan = ({ household, benefit, type, ...formProps }) => {
  const [incomes] = useStore("householdIncomes", household.id)
  const [people] = useStore("householdPeople", household.id)
  const [accounts] = useStore("householdAccounts", household.id)
  const [employers] = useStore("householdEmployers", household.id)
  const [benefits, { addBenefit, deleteBenefit, updateBenefit }] = useStore(
    "householdBenefits",
    household.id
  )

  const isNew = benefit === null
  let incomesWithBenefit = Object.values(benefits)
    .filter(({ type: benefitType }) => benefitType === type)
    .map(({ income_id }) => income_id)
  let values = {
    income_id:
      Object.values(incomes).length === 1 ? Object.values(incomes)[0].id : null
  }
  if (!isNew) {
    values = Object.assign({}, benefit)
    incomesWithBenefit = incomesWithBenefit.filter(
      id => id !== values.income_id
    )
  }
  const hasSpouse = Object.values(people).some(({ role }) => role === "spouse")
  const incomeSourceOptions = Object.values(incomes)
    .filter(({ id }) => !incomesWithBenefit.includes(id))
    .map(income => {
      const employer = Object.values(employers).find(
        ({ id }) => id === income.employer_id
      )
      const incomeName = employer ? employer.name : income.description
      const text = hasSpouse
        ? `${incomeName} (${people[income.person_id].first_name})`
        : incomeName
      return {
        text,
        value: income.id
      }
    })
  const submit = isNew
    ? async values =>
        await addBenefit({
          household_id: household.id,
          type,
          ...values
        })
    : async values =>
        await updateBenefit({
          id: benefit.id,
          type,
          ...values
        })
  const onDelete = () =>
    deleteBenefit({
      id: benefit.id,
      household_id: household.id
    })
  return (
    <SEPPlanFormContainer
      {...{
        isNew,
        submit,
        onDelete,
        incomes: Object.values(incomes),
        accounts: Object.values(accounts),
        incomeSourceOptions,
        values,
        ...formProps
      }}
    />
  )
}
export default SEPPlan
